import React from "react";

/**
 * Ten pre-baked dark-fantasy guild crests. Rendered as inline SVG so they
 * scale crisply, tint per-guild via CSS `currentColor`, and can be picked
 * from a small palette during guild creation. Keys must match the backend
 * `GUILD_LOGO_PRESETS` list.
 */

const S = ({ children, size = 48, color = "#E5C34A" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" style={{ color }}>
    {children}
  </svg>
);

const raven = (
  <>
    <path d="M8 30 Q14 12 24 14 Q34 12 40 30 L36 38 L28 32 L24 40 L20 32 L12 38 Z"
          fill="currentColor" stroke="currentColor" strokeWidth="1" />
    <circle cx="28" cy="20" r="1.5" fill="#050505" />
  </>
);

const wolf = (
  <>
    <path d="M12 14 L18 22 L16 34 L22 30 L24 36 L26 30 L32 34 L30 22 L36 14 L28 18 L24 12 L20 18 Z"
          fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="20" cy="24" r="1.5" fill="#050505" />
    <circle cx="28" cy="24" r="1.5" fill="#050505" />
  </>
);

const skull = (
  <>
    <path d="M14 18 Q14 8 24 8 Q34 8 34 18 L34 26 L30 30 L30 34 L26 34 L24 32 L22 34 L18 34 L18 30 L14 26 Z"
          fill="currentColor" />
    <circle cx="20" cy="20" r="2.5" fill="#050505" />
    <circle cx="28" cy="20" r="2.5" fill="#050505" />
    <path d="M22 26 L24 28 L26 26" stroke="#050505" strokeWidth="1.5" fill="none" />
  </>
);

const flame = (
  <>
    <path d="M24 6 Q30 14 28 20 Q36 22 34 32 Q34 42 24 42 Q14 42 14 32 Q12 22 20 20 Q18 14 24 6 Z"
          fill="currentColor" />
    <path d="M24 22 Q26 26 24 34 Q22 26 24 22" fill="#050505" opacity="0.5" />
  </>
);

const moon = (
  <>
    <path d="M18 8 A16 16 0 1 0 36 32 A12 12 0 1 1 18 8 Z" fill="currentColor" />
    <circle cx="8" cy="10" r="1" fill="currentColor" />
    <circle cx="40" cy="20" r="1.2" fill="currentColor" />
    <circle cx="12" cy="38" r="1" fill="currentColor" />
  </>
);

const shield = (
  <>
    <path d="M24 6 L38 12 L38 24 Q38 36 24 42 Q10 36 10 24 L10 12 Z"
          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
    <path d="M24 14 L24 30 M18 22 L30 22" stroke="currentColor" strokeWidth="2" />
  </>
);

const sword = (
  <>
    <path d="M24 4 L24 30 M18 30 L30 30 M20 34 L28 34 M22 38 L26 38"
          stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M24 4 L20 10 L28 10 Z" fill="currentColor" />
  </>
);

const eye = (
  <>
    <path d="M6 24 Q24 10 42 24 Q24 38 6 24 Z" fill="none" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="24" cy="24" r="6" fill="currentColor" />
    <circle cx="24" cy="24" r="2" fill="#050505" />
  </>
);

const chalice = (
  <>
    <path d="M12 10 L36 10 L34 22 Q34 30 24 32 Q14 30 14 22 Z" fill="currentColor" />
    <rect x="22" y="32" width="4" height="8" fill="currentColor" />
    <rect x="16" y="40" width="16" height="3" fill="currentColor" />
    <circle cx="24" cy="18" r="2" fill="#050505" opacity="0.5" />
  </>
);

const crown = (
  <>
    <path d="M6 32 L12 14 L18 24 L24 10 L30 24 L36 14 L42 32 Z" fill="currentColor" />
    <rect x="8" y="34" width="32" height="4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="#8A0303" />
    <circle cx="24" cy="8" r="1.8" fill="#8A0303" />
    <circle cx="36" cy="12" r="1.5" fill="#8A0303" />
  </>
);

const LOGO_MAP = { raven, wolf, skull, flame, moon, shield, sword, eye, chalice, crown };

export const GUILD_LOGO_KEYS = Object.keys(LOGO_MAP);

export default function GuildLogo({ logo = "raven", size = 48, color = "#E5C34A" }) {
  const svg = LOGO_MAP[logo] || raven;
  return <S size={size} color={color}>{svg}</S>;
}
